import { Box, Divider, Modal, Typography, useMediaQuery } from '@mui/material';
import { isEmpty } from 'lodash';
import React from 'react';
import Button from '../../../components/Button';
import {
  addInstructionsApi,
  updateInstructionsApi,
  addUserInstructionsApi,
  updateUserInstructionsApi,
  signedUserUrlApi,
} from '../../../utils/api';
import { useMutation } from 'react-query';
import useStore from '../../../Store/Store';
import TextInput from '../../../components/TextInput';
import { ALLOWED_FILES_DOC, MAX_FILE_SIZE, MAX_VIDEO_SIZE } from '../../../constants';
import axios from 'axios';
import { toast } from 'react-toastify';
import Progressbar from '../../../components/Progressbar';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const AddInstructionsModal = ({ show, onClose, recipient, refetch, activeEditData }) => {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [state, setState] = React.useState({
    title: '',
    description: '',
    link: '',
    assignedTo: '',
    errorMessage: '',
  });
  const [imageLoader, setImageLoader] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (!isEmpty(activeEditData)) {
      setState((prev) => {
        return {
          ...prev,
          title: activeEditData.title,
          description: activeEditData.description,
          link: activeEditData.link,
          assignedTo: activeEditData.assignedTo,
        };
      });
    }
  }, [activeEditData]);

  const addInstructionHandler = useMutation(recipient?._id ? addInstructionsApi : addUserInstructionsApi, {
    onSuccess: async ({ data }) => {
      setState({
        title: '',
        description: '',
        link: '',
        assignedTo: '',
        errorMessage: '',
      });
      refetch();
      onClose();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    },
  });

  const updateInstructionHandler = useMutation(recipient?._id ? updateInstructionsApi : updateUserInstructionsApi, {
    onSuccess: async ({ data }) => {
      setState({
        title: '',
        description: '',
        link: '',
        assignedTo: '',
        errorMessage: '',
      });
      refetch();
      onClose();
    },
    onError: (error) => {
      console.log('error =====> ', error);
      setState((prev) => {
        return { ...prev, errorMessage: error?.response?.data?.message || 'Something went wrong!' };
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const { title, description, link, assignedTo } = state;
    if (isEmpty(title) || isEmpty(description)) {
      setState((prev) => {
        return { ...prev, errorMessage: 'Fill all the fields' };
      });
      return;
    }

    const body = {
      title,
      description,
      link,
      assignedTo,
    };

    if (!isEmpty(activeEditData)) {
      updateInstructionHandler.mutate({
        userId: user?._id,
        recipientId: recipient?._id,
        instructionId: activeEditData?._id,
        body,
      });
    } else {
      addInstructionHandler.mutate({
        userId: user?._id,
        recipientId: recipient?._id,
        body,
      });
    }
  };

  const onCloseModal = () => {
    setState({
      title: '',
      description: '',
      link: '',
      assignedTo: '',
      errorMessage: '',
    });
    onClose();
  };

  const handleUploadVideo = async (e) => {
    setImageLoader(true);
    try {
      const [file] = e.target.files;
      const { type, size } = file;

      if (size > MAX_VIDEO_SIZE) {
        toast.error('Max file size 10MB!');
        setImageLoader(false);
        return;
      }

      if (!ALLOWED_FILES_DOC.videos.includes(type)) {
        toast.error('File not supported!');
        setImageLoader(false);
        return;
      }
      let postURL = '';
      if (recipient?._id) {
        const { data } = await axios.post(
          `${process.env.REACT_APP_EXPRESS_URL}/api/canCare/caregiver/${user?._id}/signedUrl/${recipient?._id}`,
          { type, isDocuments: false },
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: localStorage.getItem('auth'),
              'ngrok-skip-browser-warning': 'true',
            },
          },
        );
        postURL = data?.data?.postURL;
      } else {
        const { data } = await signedUserUrlApi({ userId: user?._id, type, isDocuments: false });
        postURL = data?.postURL;
      }
      await axios.put(postURL, file, {
        headers: {
          'Content-Type': type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setProgress(percentCompleted);
        },
      });

      setState((prev) => {
        return { ...prev, link: postURL.split('?')[0] };
      });
    } catch (error) {
      toast.error('Something went wrong!');
      console.log('error =====> ', error);
    } finally {
      setImageLoader(false);
    }
  };

  return (
    <Modal
      open={show}
      onClose={onCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      BackdropProps={{ onClick: () => {} }}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '70%' : '30%',
          p: 6,
          bgcolor: 'background.paper',
          borderRadius: 2.5,
          // position: 'relative',
          // top: 50,
          // left: isMobile ? '4%' : '35%',
        }}
      >
        <Typography variant="h5" textAlign="center" marginBottom={'20px'}>
          {t('addInstruction')}
        </Typography>
        <TextInput
          label={t('title')}
          value={state.title}
          onChange={(e) =>
            setState((prev) => {
              return {
                ...prev,
                title: e.target.value,
              };
            })
          }
        />
        <TextInput
          label={t('description')}
          multiline
          value={state.description}
          onChange={(e) =>
            setState((prev) => {
              return {
                ...prev,
                description: e.target.value,
              };
            })
          }
        />

        <TextInput type="file" onChange={handleUploadVideo} />
        {imageLoader && <Progressbar value={progress} />}

        <Typography variant="subtitle1" style={{ color: 'red', marginTop: '30px' }} textAlign="center">
          {state.errorMessage && t(state.errorMessage)}
        </Typography>

        <Divider
          sx={{
            marginTop: 2,
            borderColor: '#E0E0E0',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: 2,
          }}
        >
          <Button text={t('cancel')} onClick={onCloseModal} bg="light" />
          <Button text={t('submit')} onClick={handleSubmit} loading={imageLoader || addInstructionHandler.isLoading} />
        </Box>
      </Box>
    </Modal>
  );
};

export default AddInstructionsModal;
